import * as Logger from './logger.js'
import writeExcelFile from 'write-excel-file';
import * as Utils from './utils.js';
import { Auth } from './auth.js';
import { UserSession } from './session.js';
import { jsonDiff } from './utils.js';
import { HSComponent } from './component.js';
import { HSCard } from './card.js';
import { hasCapability } from './capabilities.js';
import styles from './app.css';
import template from './app.html';
import { HSSidebar } from './sidebar.js';
import { HSPage } from './page.js';
import { HSBlade } from './blade.js';
import { HSExplorer } from './explorer.js';
import { HSLogViewer } from './log-viewer.js';
import { HSPipelineSandbox } from './pipeline-sandbox.js';
import { HSServiceMonitor } from './service-monitor.js';

class HSApp extends HSComponent {
    config = {};
    page;
    explorer = null;
    logViewer = null;
    dashboardView = 'default';
    versionPollTimer = null;
    versionPollIntervalMs = 300000;
    versionStaleNotified = false;

    constructor() { 
        super();
    }

    onLoad() {
        Logger.debug("App init");
        UserSession.init();
        this.showLoading();
        this.login();
    }

    onUnload() {
        if (this.versionPollTimer) {
            window.clearInterval(this.versionPollTimer);
            this.versionPollTimer = null;
        }
        super.onUnload();
    }

    async fetchVersionInfo() {
        const cacheBust = Date.now();
        return await this.fetch(`/api/version?_=${cacheBust}`);
    }

    updateVersionSessionState({ state, message = '', baseline = null, latest = null } = {}) {
        const next = {
            state,
            message,
            baseline,
            latest,
            updatedAt: Date.now(),
        };
        UserSession.set('appVersionStatus', next);
        window.dispatchEvent(new CustomEvent('hs-version-status', { detail: next }));
    }

    async startVersionMonitor() {
        if (this.versionPollTimer) {
            window.clearInterval(this.versionPollTimer);
            this.versionPollTimer = null;
        }

        try {
            const initial = await this.fetchVersionInfo();
            if (!initial?.version) {
                Logger.warn('version monitor: /api/version did not return a version field');
                return;
            }
            UserSession.set('appVersionBaseline', initial);
            this.versionStaleNotified = false;
            this.updateVersionSessionState({
                state: 'ok',
                message: `v${initial.version}`,
                baseline: initial,
                latest: initial,
            });
        } catch (err) {
            Logger.warn('version monitor: initial fetch failed', err);
            return;
        }

        this.versionPollTimer = window.setInterval(async () => {
            await this.checkVersionForUpdate();
        }, this.versionPollIntervalMs);
    }

    async checkVersionForUpdate() {
        const baseline = UserSession.get('appVersionBaseline');
        if (!baseline?.version) {
            return;
        }

        try {
            const latest = await this.fetchVersionInfo();
            if (!latest?.version) {
                return;
            }
            if (String(latest.version) !== String(baseline.version)) {
                const message = `A newer dashboard version is available (${baseline.version} -> ${latest.version}). Reload when ready.`;
                this.updateVersionSessionState({
                    state: 'stale',
                    message,
                    baseline,
                    latest,
                });
                if (!this.versionStaleNotified) {
                    this.versionStaleNotified = true;
                    Logger.warn('version monitor: frontend build is stale', { baseline, latest });
                    window.dispatchEvent(new CustomEvent('hs-version-stale', {
                        detail: {
                            message,
                            baseline,
                            latest,
                        },
                    }));
                }
            }
        } catch (err) {
            Logger.debug('version monitor: periodic check failed', err);
        }
    }

    canEditDashboards(userinfo = UserSession.get('userinfo')) {
        return hasCapability(userinfo, 'dashboard.custom.manage');
    }

    setDashboardView(view) {
        this.dashboardView = view;
        UserSession.set('dashboard_view', view);
    }

    setEditMode(enabled) {
        this.toggleEditMode(enabled);
        UserSession.set('edit_mode', !!enabled);
    }

    async onLogin(userinfo) {
        if (!userinfo) {
            // just return.  this is normal behavior during logout or auto-redirect, we can safely ignore while the keycloak client
            // is executing the redirect
            Logger.warn("redirecting...");
            return;
        }
        Logger.debug("userinfo: ", userinfo);

        this.loadTemplate(template);
        this.loadStyles(styles);

        UserSession.set('edit_mode', false);
        UserSession.set('can_edit_dashboard', this.canEditDashboards(userinfo));

        this.onEvent("hs-toggle-edit-mode", () => {
            if (!this.canEditDashboards() || this.dashboardView !== 'custom') {
                Logger.warn('ignoring edit-mode toggle while not in editable custom dashboard mode');
                this.setEditMode(false);
                return;
            }
            this.setEditMode(!this.editMode);
        });
        this.onEvent("hs-load-dashboard-custom", async () => {
            await this.loadCustomDashboard();
        });
        this.onEvent("hs-load-dashboard-default", async () => {
            await this.loadDefaultDashboard();
        });
        this.onEvent("hs-reset-dashboard", async () => {
            await this.resetDashboardToDefault();
        });
        this.onEvent("hs-load-blade", opts => this.loadBlade(opts));
        this.onEvent("hs-unload-blade", opts => this.unloadBlade(opts));
        this.onEvent("hs-save-config", opts => this.saveDashboard());
        this.onEvent("hs-export-data", opts => this.exportData());
        this.onEvent("hs-logout", opts => Auth.logout());
        this.onEvent("hs-open-explorer", () => this.openExplorer());
        this.onEvent("hs-close-explorer", () => this.closeExplorer());
        this.onEvent("hs-open-log-viewer", () => this.openLogViewer());
        this.onEvent("hs-close-log-viewer", () => this.closeLogViewer());
        this.onEvent("hs-open-pipeline-sandbox", () => this.openPipelineSandbox());
        this.onEvent("hs-close-pipeline-sandbox", () => this.closePipelineSandbox());
        this.onEvent("hs-open-service-monitor", () => this.openServiceMonitor());
        this.onEvent("hs-close-service-monitor", () => this.closeServiceMonitor());
        this.onEvent("hs-auth-invalid", ({ status, reason }) => {
            Logger.error('Received auth-invalid event from SSE layer', { status, reason });
            alert('Your session is no longer valid. Please sign in again.');
            Auth.logout();
        }, window);
        this.onEvent('hs-version-stale', ({ message }) => {
            if (message) {
                alert(message);
            }
        }, window);
        this.onEvent("hs-cloud-select", async opts => {
            Logger.debug("cloud-select: ", opts);
            const selected_clouds = Array.isArray(opts.selected) ? opts.selected : [ opts.selected ];
            if (selected_clouds == 0) {
                // user removed all selections, revert back to placeholder and show current cloud (TODO: show all clouds merged)
                const current_cloud = UserSession.get("current_cloud");
                UserSession.set("selected_clouds", current_cloud ? [ current_cloud ] : []);
            } else {
                // user selected a cloud
                UserSession.set("selected_clouds", selected_clouds);
            }
            // reload the orgs and providers accordingly and reset all filters
            await UserSession.reloadData();
            UserSession.set("filtered_orgs", UserSession.get("orgs"));
            UserSession.set("selected_orgs", []);
            UserSession.set("filtered_providers", UserSession.get('providers'));
            UserSession.set("selected_providers", []);
            if (this.explorer?.reloadData) {
                await this.explorer.reloadData();
            }
            this.reloadData();
        });
        this.onEvent("hs-org-select", opts => {
            Logger.debug("org-select: ", opts);
            const selected_orgs = Array.isArray(opts.selected) ? opts.selected : [ opts.selected ];
            if (selected_orgs == 0) {
                // user removed all selections, revert back to placeholder and expand providers selection
                UserSession.set("selected_orgs", []);
                UserSession.set("filtered_providers", UserSession.get('providers').filter(x => UserSession.get('filtered_orgs').find(o => o.id === x.orgid)));
            } else {
                // user selected an org, filter the providers accordingly
                UserSession.set("selected_orgs", selected_orgs);
                UserSession.set("filtered_providers", UserSession.get('providers').filter(x => selected_orgs.find(o => o.id === x.orgid)));
            }
            if (UserSession.get('selected_providers').length > 0) {
                // filter the selected providers to only those that are in the filtered list
                UserSession.set("selected_providers", UserSession.get('selected_providers').filter(x => UserSession.get('filtered_providers').find(p => p.id === x)));
            }
            this.reloadData();
        });
        this.onEvent("hs-provider-select", async opts => {
            Logger.debug("provider-select event: ", opts);
            try {
                const selected_providers = Array.isArray(opts.selected) ? opts.selected : [ opts.selected ];
                UserSession.set("selected_providers", selected_providers);
                await this.reloadData();

                // Preserve current page/tab after provider changes. The only exception is
                // provider drilldown while on the Providers metric tab, where we advance
                // to Accounts so content stays meaningful once the Providers metric becomes a link card.
                const selectedTabs = UserSession.get('selected_tabs') || {};
                const activeMetricsTabId = selectedTabs.metricsTabs
                    || this.page?.gridstack?.findActiveTab('metricsTabs')?.config?.id
                    || null;
                const inProvidersTab = activeMetricsTabId === 'providers';
                const singleProviderSelected = selected_providers.length === 1;
                if (inProvidersTab && singleProviderSelected) {
                    this.page?.gridstack?.showTabContent({ id: 'accounts', tabGroup: 'metricsTabs' });
                }
            } catch (err) {
                Logger.error("provider-select failed: ", err);
            }
        });

        if (this.canEditDashboards(userinfo)) {
            this.loadCustomDashboard();
        } else {
            this.loadDefaultDashboard();
        }

        this.startVersionMonitor();
    }

    async loadCustomDashboard() {
        const userId = UserSession.get('userinfo')?.sub;
        if (!userId) {
            Logger.warn('missing user id, cannot load custom dashboard');
            return;
        }
        this.setEditMode(false);
        await this.loadDashboard(userId, { view: 'custom', fallbackToDefault: true });
    }

    async loadDefaultDashboard() {
        this.setEditMode(false);
        await this.loadDashboard('default', { view: 'default', fallbackToDefault: true });
    }

    normalizeProvidersSummaryCards(config = this.config) {
        const normalizeMetricsGrid = (grid) => {
            if (grid?.id !== 'metrics' || !Array.isArray(grid.cards)) {
                return;
            }
            const providersCards = grid.cards.filter(card => card?.id === 'providers');
            const linkCard = providersCards.find(card => card?.type === 'hs-link-card');
            const metricCard = providersCards.find(card => card?.type === 'hs-metric');

            if (linkCard) {
                // Show link-card only when exactly one provider is selected within
                // a multi-provider scope (drilldown state).
                linkCard.displayIf = [
                    [ "${session.get('selected_providers')?.length == 1}", "==", "true" ],
                    "and",
                    [ "${session.get('providers')?.length > 1}", "==", "true" ]
                ];
            }

            if (metricCard) {
                // Show metric-card in broad provider scope, including the single-provider
                // case, so users can still open the Providers tab/page.
                metricCard.displayIf = [
                    [ "${session.get('providers')?.length == 1}", "==", "true" ],
                    "or",
                    [
                        [ "${session.get('selected_providers')?.length != 1}", "==", "true" ],
                        "and",
                        [ "${session.get('providers')?.length > 1}", "==", "true" ]
                    ]
                ];
            }
        };

        const walkGrids = (grids = []) => {
            for (const grid of grids) {
                normalizeMetricsGrid(grid);
                walkGrids(grid?.grids || []);
            }
        };

        for (const page of config?.app?.pages || []) {
            walkGrids(page?.grids || []);
        }
    }

    async loadDashboard(id = UserSession.get('userinfo')?.sub, { view = 'custom', fallbackToDefault = true } = {}) {
        this.setDashboardView(view);
        try {
            this.config = await this.fetch('/conf/dashboards/' + id, {
                expectedStatuses: fallbackToDefault ? [ 404 ] : [],
            });
            this.normalizeProvidersSummaryCards(this.config);
            Logger.debug("got user dashboard: ", this.config);
            const pageId = this.config?.app?.pages?.find(x => x.selected === true)?.id;
            this.loadPage(pageId);
        } catch(err) {
            const status = Number(
                err?.status
                ?? err?.response?.status
                ?? err?.response?.statusCode
                ?? err?.cause?.status
                ?? NaN
            );
            const looksLikeNotFound = status === 404 || /\b404\b|not\s*found/i.test(String(err?.message || ''));
            Logger.debug('loadDashboard fallback check: ', { requestedId: id, fallbackToDefault, status, looksLikeNotFound });
            if (fallbackToDefault && looksLikeNotFound) {
                Logger.info('custom dashboard missing; falling back to default dashboard', { requestedId: id, status });
                this.config = await this.fetch('/conf/dashboards/default');
                this.normalizeProvidersSummaryCards(this.config);
                Logger.debug("got default dashboard: ", this.config);
                this.loadPage();
            } else {
                Logger.warn("fetch error: ", err);
                throw err;
            }
        }
        this.hideLoading();
    }

    async saveDashboard(id = UserSession.get('userinfo')?.sub) {
        if (!this.canEditDashboards()) {
            Logger.warn('ignoring save request for non-editable user');
            return;
        }
        if (this.dashboardView !== 'custom') {
            Logger.warn('ignoring save request while viewing default dashboard');
            return;
        }
        if (!id) {
            throw new Error("can't save without valid id"); 
        }
        Logger.debug("saving dashboard config: ", id);
        const config = structuredClone(this.config);
        config.modified_time = new Date().toISOString();
        if (this.page) {
            const pageIndex = config.app.pages.findIndex(x => x.id === this.page.id);
            if (pageIndex >= 0) {
                const page_config = this.page.saveConfig();
                Logger.debug("page config: ", page_config);
                if (page_config) {
                    config.app.pages[pageIndex] = page_config;
                }
            }
        }
        //if (window.location.href.includes('localhost')) { // local dev server doesn't support save } else {
        await this.put('./conf/dashboards/' + id, config);
        // notify all child components that we have successfully saved our state
        const components = this.findChildren({ predicate: child => child instanceof HSComponent, shadowOnly: false });
        Logger.debug("found components: ", components);
        components.forEach(c => c.configSaved());
    }

    async resetDashboardToDefault() {
        if (!this.canEditDashboards()) {
            Logger.warn('ignoring reset dashboard request for non-editable user');
            return;
        }
        const userId = UserSession.get('userinfo')?.sub;
        if (!userId) {
            Logger.warn('missing user id, cannot reset dashboard');
            return;
        }
        this.showLoading();
        try {
            const defaultConfig = await this.fetch('/conf/dashboards/default');
            const resetConfig = structuredClone(defaultConfig);
            resetConfig.modified_time = new Date().toISOString();
            await this.put('./conf/dashboards/' + userId, resetConfig);
            Logger.info('reset dashboard to default for user: ', userId);
            await this.loadCustomDashboard();
        } finally {
            this.hideLoading();
        }
    }

    // download contents as an XLSX file
    async exportData(filename = 'dashboard.xlsx') {
        this.showLoading();
        try {
            const toCell = (value, isHeader = false) => {
                if (value === undefined || value === null) {
                    return { type: String, value: '' };
                }
                // For numeric values (already numbers or numeric strings), use Number type
                const numVal = Number(value);
                if (Number.isFinite(numVal) && !isHeader) {
                    return { type: Number, value: numVal };
                }
                // For everything else (headers, non-numeric strings), use String type
                return { type: String, value: String(value) };
            };
            const toSheetRows = (headers, rows) => {
                const headerRow = headers.map(h => toCell(h, true));
                const dataRows = rows.map((row) => row.map(cell => toCell(cell, false)));
                return [ headerRow, ...dataRows ];
            };

            const sheetNames = [];
            const sheetColumns = [];
            const sheetData = [];
            const dataSections = [];
            const mainCols = [];
            const mainObj = {};
            const mainRows = [];
            let showOrgCol = false;
            const userinfo = UserSession.get('userinfo');
            if (userinfo.usertype == 'ops') {
                showOrgCol = true;  // for now, just always show org column for ops reports
            }

            const providers = UserSession.get("selected_providers")?.length > 0 ? 
                UserSession.get("selected_providers") :
                UserSession.get("filtered_providers");

            if (showOrgCol) {
                mainCols.push({ header: 'Org', key: 'org', width: 20 });
            }
            mainCols.push({ header: 'Selected Providers', key: 'provider', width: 28 });
            let first = true;
            for (const provider of providers) {
                if (first) {
                    first = false;
                    if (showOrgCol) { 
                        mainObj['org'] = provider.orgdisplayname ?? provider.orgname;
                    }
                    mainObj['provider'] = provider.displayname;
                } else {
                    mainRows.push({ org: provider.orgdisplayname ?? provider.orgname, provider: provider.displayname });
                }
            }

            const components = this.findChildren({ predicate: child => child instanceof HSCard, shadowOnly: false });
            const exportValueCards = components.filter(component => component.config.exportable && typeof component.getExportValues === "function");
            const exportValueY = exportValueCards
                .map(component => Number(component?.config?.y))
                .filter(Number.isFinite);
            const topMetricsY = exportValueY.length > 0 ? Math.min(...exportValueY) : null;
            for (const component of components) {
                // if the component has exportable values, we add to the main sheet
                if (component.config.exportable && typeof component.getExportValues === "function") {
                    const componentY = Number(component?.config?.y);
                    const includeInTopMetrics = topMetricsY === null || !Number.isFinite(componentY) || componentY === topMetricsY;
                    if (!includeInTopMetrics) {
                        continue;
                    }
                    const exportValues = component.getExportValues();
                    for (const item of exportValues) {
                        const key = item.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                        mainCols.push({ header: item.name, key, width: 20 });
                        mainObj[key] = item.value;
                    }
                }
                // if the component has exportable data, we save that as a separate dedicated sheet
                if (component.config.exportable && typeof component.getExportData === "function") {
                    const data = component.getExportData();
                    Logger.debug("adding xlsx export section: ", data?.name);
                    const sectionColumns = (data?.cols || []).map((col) => ({
                        width: col.width || 20
                    }));
                    const sectionHeaders = (data?.cols || []).map(col => col.header || col.name || col.key || '');
                    const sectionKeys = (data?.cols || []).map(col => col.key);
                    const sectionRows = (data?.rows || []).map(row => sectionKeys.map(key => row?.[key]));
                    if (sectionRows.length > 0 && sectionColumns.length > 0) {
                        const sectionName = (data?.name || 'Data').slice(0, 31).replace(/[\\/?*[\]:]/g, '_');
                        const sectionMeta = (data?.meta || []).map(text =>
                            Array(sectionColumns.length).fill(null).map((_, i) => toCell(i === 0 ? text : '', true))
                        );
                        const sectionBody = [...sectionMeta, ...toSheetRows(sectionHeaders, sectionRows)];
                        dataSections.push({
                            name: sectionName,
                            columns: sectionColumns,
                            rows: sectionRows.length,
                            body: sectionBody,
                        });
                    }
                }
            }

            // Build top-metrics summary sheet.
            const mainColumns = mainCols.map((col) => ({
                width: col.width || 20
            }));
            const mainHeaders = mainCols.map(col => col.header);
            const mainKeys = mainCols.map(col => col.key);
            const normalizedMainData = [ mainObj, ...mainRows ].map(row => mainKeys.map(key => row?.[key]));

            const topMetricsSheet = {
                name: 'Top Metrics',
                columns: mainColumns,
                body: toSheetRows(mainHeaders, normalizedMainData),
            };

            // Sheet order policy:
            // 1) Main content data sheet first (largest export-data section)
            // 2) Top metrics summary second
            // 3) Remaining data sheets afterwards
            if (dataSections.length > 0) {
                let primaryIndex = 0;
                for (let i = 1; i < dataSections.length; i++) {
                    if (Number(dataSections[i].rows) > Number(dataSections[primaryIndex].rows)) {
                        primaryIndex = i;
                    }
                }

                const primary = dataSections[primaryIndex];
                const rest = dataSections.filter((_, idx) => idx !== primaryIndex);
                const ordered = [ primary, topMetricsSheet, ...rest ];

                for (const section of ordered) {
                    sheetNames.push(section.name);
                    sheetColumns.push(section.columns);
                    sheetData.push(section.body);
                }
            } else {
                sheetNames.push(topMetricsSheet.name);
                sheetColumns.push(topMetricsSheet.columns);
                sheetData.push(topMetricsSheet.body);
            }

            // Write XLSX file
            await writeExcelFile(sheetData, {
                sheets: sheetNames,
                columns: sheetColumns,
                fileName: filename
            });
            this.hideLoading();
            return false;
        } catch (err) {
            Logger.error('Export failed: ', err);
            this.hideLoading();
            throw err;
        }
    }

    loadPage(pageRef = null) {
        const pages = this.config?.app?.pages || [];
        if (!Array.isArray(pages) || pages.length < 1) {
            Logger.error("page not found: no pages available in config");
            return;
        }

        // allow caller to pass either an id string, a page object, or nothing.
        let pageId = null;
        if (typeof pageRef === 'string') {
            pageId = pageRef;
        } else if (pageRef && typeof pageRef === 'object') {
            pageId = pageRef.id;
        }
        if (!pageId) {
            pageId = pages.find(x => x.selected === true)?.id || pages[0]?.id;
        }

        const config = pages.find(x => x.id === pageId) || pages[0];
        if (!config) {
            Logger.error("page not found: ", pageId);
            return;
        }
        // for now, we keep the page and replace it's contents.  in future, we may
        // decide to create a new hs-page instead...
        this.page = this.shadowRoot.querySelector("hs-page");
        this.page.setAttribute('id', config.id);
        this.page.init(structuredClone(config));
    }

    loadBlade({ id } = {}) {
        const config = this.config?.app?.blades?.find(x => x.id === id);
        if (!config) {
            Logger.error("blade not found: ", id);
            return;
        }
        const blade = document.createElement('hs-blade');
        Logger.debug("blade: ", blade);
        blade.editMode = this.editMode;
        this.appendChild(blade);
        blade.init(config);
    }

    unloadBlade({ blade }) {
        this.removeChild(blade);
    }

    openExplorer() {
        if (this.explorer) return;  // already open
        this.explorer = document.createElement('hs-explorer');
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = 'none';
        }
        if (this.page) {
            this.page.appendChild(this.explorer);
        } else {
            this.appendChild(this.explorer);
        }
    }

    closeExplorer() {
        if (!this.explorer) return;
        if (this.explorer.parentNode) {
            this.explorer.parentNode.removeChild(this.explorer);
        }
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = '';
            this.page.gridstack.scheduleResponsiveLayout?.();
        }
        this.explorer = null;
    }

    openLogViewer() {
        if (this.logViewer) return;  // already open
        this.logViewer = document.createElement('hs-log-viewer');
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = 'none';
        }
        if (this.page) {
            this.page.appendChild(this.logViewer);
        } else {
            this.appendChild(this.logViewer);
        }
    }

    closeLogViewer() {
        if (!this.logViewer) return;
        if (this.logViewer.parentNode) {
            this.logViewer.parentNode.removeChild(this.logViewer);
        }
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = '';
            this.page.gridstack.scheduleResponsiveLayout?.();
        }
        this.logViewer = null;
    }

    openPipelineSandbox() {
        if (this.pipelineSandbox) return;
        this.pipelineSandbox = document.createElement('hs-pipeline-sandbox');
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = 'none';
        }
        if (this.page) {
            this.page.appendChild(this.pipelineSandbox);
        } else {
            this.appendChild(this.pipelineSandbox);
        }
    }

    closePipelineSandbox() {
        if (!this.pipelineSandbox) return;
        if (this.pipelineSandbox.parentNode) {
            this.pipelineSandbox.parentNode.removeChild(this.pipelineSandbox);
        }
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = '';
            this.page.gridstack.scheduleResponsiveLayout?.();
        }
        this.pipelineSandbox = null;
    }

    openServiceMonitor() {
        if (this.serviceMonitor) return;
        this.serviceMonitor = document.createElement('hs-service-monitor');
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = 'none';
        }
        if (this.page) {
            this.page.appendChild(this.serviceMonitor);
        } else {
            this.appendChild(this.serviceMonitor);
        }
    }

    closeServiceMonitor() {
        if (!this.serviceMonitor) return;
        if (this.serviceMonitor.parentNode) {
            this.serviceMonitor.parentNode.removeChild(this.serviceMonitor);
        }
        if (this.page?.gridstack) {
            this.page.gridstack.style.display = '';
            this.page.gridstack.scheduleResponsiveLayout?.();
        }
        this.serviceMonitor = null;
    }

    async reloadData() {
        Logger.debug("reloading data...");
        Logger.debug("selected providers: ", UserSession.get("selected_providers"));
        Logger.debug("selected timeScope: ", UserSession.get("timeScope"));
        // tell the page to reload it's data
        await this.page.reloadData();
        // tell any other components we manage in our own shadowRoot DOM to reload their data
        const components = this.findChildren({ 
            predicate: (child) => child instanceof HSComponent && !(child instanceof HSPage),
            shadowOnly: true, 
        });
        for (const component of components) {
            if (typeof component.reloadData === "function") {
                component.reloadData();
            }
        }
        
        // TODO: we can make this a subscription that gets live updates instead
        // we want to reload the providers list in case the user has been invited to new providers, but we
        // do it silently in the background and not block the page load
        setTimeout(async () => {
            await UserSession.reloadData();
        }, 1000); // wait a bit to allow the page to finish loading

        /*
        // clear any SSE subscriptions for closed Cards
        setTimeout(() => {
            const cards = this.findChildren({ predicate: (child) => child instanceof HSCard, shadowOnly: false });
            const cardSubscriptions = cards.filter(card -> card.config.src.subscribe).map(card => card.sseSubscriptionId);
            Logger.debug("cardSubscriptions: ", cardSubscriptions);
            const activeSubscriptions = UserSession.get('sseSubscriptions') || {};
            Logger.debug("activeSubscriptions: ", Object.keys(activeSubscriptions));
            const syncSubscriptions = 
        }, 2000);
        */
    }

    async login() {
        Logger.debug("getting env...");
        const host_url = new URL(window.location.href);
        const normalizeAuthBaseUrl = (rawUrl) => {
            try {
                const url = new URL(rawUrl);
                // Keep a root/auth base URL but strip any realm-specific suffix.
                // Use a local variable to avoid URL.pathname normalization re-adding '/'.
                const cleanedPath = (url.pathname || '')
                    .replace(/\/realms\/[^/]+\/?$/i, '')
                    .replace(/\/+$/, '');
                return `${url.origin}${cleanedPath}/`;
            } catch (err) {
                Logger.warn('invalid authUrl in env; using raw value', { rawUrl, err: err?.message || err });
                return rawUrl;
            }
        };
        // TODO: we can enhance the backend to support global redirect.  this would allow a customer to
        // have providers in multiple clouds, and the idpHint would redirect using an IDP to a different
        // cloud's keycloak server to allow all global users to exist in a single authentcation realm
        const env = await this.fetch('/env.json');
        Logger.debug("env: ", env);
        const hostname = host_url.hostname;
        const host_domain = hostname.split(".")[0];
        const idpHint = env.idpHint || host_domain.toUpperCase().replace('-', '_');
        Logger.debug("idpHint: ", idpHint);
        const realm = 'HOMESIGHT';
        const clientId = idpHint === 'OPS' ? 'ops-user-client' : 'public-user-client';
        const authUrl = normalizeAuthBaseUrl(env.authUrl);
        const options = { realm, url: authUrl, idpHint, clientId };
        Logger.debug("authenticating...", options);
        try {
            const userinfo = await Auth.login(options);
            this.onLogin(userinfo);
        } catch (err) {
            Logger.error("failed to initialize authentication client", err);
            alert('failed to initialize authentication client');
        }
    }
}
customElements.define('hs-app', HSApp);
